import { useEffect, useRef } from "react";
import { useEditorState } from "@/store";
import { idleShutDown } from "@/api";
import { FS } from "@/singletons/FileSystem";
import sessionSocket from "@/singletons/SessionSocket";

/**
 * Desktop-only. Gives the editor a chance to finish what it was doing before
 * the Electron shell destroys the window.
 *
 * On the web this work is driven by `beforeunload`, where the browser shows a
 * confirmation dialog and grants the page time to finish. Electron has no such
 * dialog, and the shell force-destroys the window -- which skips `beforeunload`
 * and `unload` entirely -- so nothing that hangs off those events runs. That
 * includes the pending-upload flush in FileSystem and the socket teardown in
 * SessionSocket.
 *
 * Feature-detected on `window.electron`: on the web the listener is never
 * registered and this component does nothing.
 */
export default function AppCloseGuard() {
  const ranRef = useRef(false);

  useEffect(() => {
    const handler = async () => {
      if (ranRef.current) return;
      ranRef.current = true;

      window.logBridge?.sendLog("[AppCloseGuard] app-before-close sinyali alındı");

      try {
        // Uploads first: idleShutDown stops the session, so anything still
        // waiting to reach S3 has to go out before that. This is the step that
        // was missing -- without it a student who closes the app loses whatever
        // had not been uploaded yet, with no warning of any kind.
        const pending = FS.utils.getPendingRemoteWrites().length;
        if (pending > 0) {
          window.logBridge?.sendLog(`[AppCloseGuard] ${pending} bekleyen yükleme, flush ediliyor`);
          await FS.utils.leaveListener();
          window.logBridge?.sendLog("[AppCloseGuard] flush tamamlandı");
        }

        // Store'dan en güncel state'i çek
        const currentAccessToken = useEditorState.getState().tokens?.express_api_token;
        if (currentAccessToken) {
          window.logBridge?.sendLog("[AppCloseGuard] idleShutDown çağrılıyor");
          await idleShutDown(currentAccessToken);
          window.logBridge?.sendLog("[AppCloseGuard] idleShutDown tamamlandı");
        } else {
          window.logBridge?.sendLog("[AppCloseGuard] accessToken yok, idleShutDown atlandı");
        }

        sessionSocket.disconnect();
      } catch (err) {
        window.logBridge?.sendLog(
          "[AppCloseGuard] Hata: " + (err instanceof Error ? err.message : String(err))
        );
      } finally {
        // Must always fire. If it does not, the shell waits out its timeout and
        // the user watches the window hang for several seconds before it closes.
        window.logBridge?.sendLog("[AppCloseGuard] main'e done sinyali gönderiliyor");
        window.electron?.ipcRenderer?.send("app-before-close-done");
      }
    };

    window.electron?.ipcRenderer?.on("app-before-close", handler);
    return () => {
      window.electron?.ipcRenderer?.removeListener("app-before-close", handler);
    };
  }, []);

  return null;
}
